import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

type ToastTone = 'success' | 'info' | 'danger';

interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
}

const ToastContext = createContext<((opts: ToastOptions) => void) | null>(null);

const TONE_META: Record<ToastTone, { icon: string; color: string }> = {
  success: { icon: '✓', color: 'var(--iris)' },
  info: { icon: 'ℹ', color: 'var(--info)' },
  danger: { icon: '✕', color: 'var(--danger)' },
};

const DISMISS_MS = 4000;

/**
 * App-level provider for non-blocking, auto-dismissing toasts. Wrap the app once,
 * then call {@link useToast} anywhere to surface a brief confirmation. Toasts
 * stack bottom-right, announce politely to assistive tech, and fade after 4s.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description, tone = 'success' }: ToastOptions) => {
      const id = nextId.current++;
      setToasts((cur) => [...cur, { id, title, description, tone }]);
      setTimeout(() => dismiss(id), DISMISS_MS);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((t) => {
          const meta = TONE_META[t.tone];
          return (
            <div
              key={t.id}
              role="status"
              aria-live="polite"
              className="flex items-start gap-2.5 rounded-md border border-hairline bg-surface p-3 shadow-pop"
              style={{ borderLeft: `3px solid ${meta.color}` }}
            >
              <span className="mt-0.5 text-sm font-bold" style={{ color: meta.color }} aria-hidden>
                {meta.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-bone">{t.title}</p>
                {t.description && (
                  <p className="mt-0.5 text-xs leading-relaxed text-muted">{t.description}</p>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="flex-none text-xs text-muted transition-colors hover:text-bone"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/** Surface a non-blocking toast. Must be used within {@link ToastProvider}. */
export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast must be used within a ToastProvider');
  return { toast };
}
